import { Fragment, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import {
  CalendarDays,
  Check,
  FileText,
  HelpCircle,
  MessageSquare,
  PawPrint,
  Pencil,
  Save,
  Send,
  ShieldCheck,
  UserRound,
} from "lucide-react";

import { useAnimalPhotoUrls } from "../../api/useAnimalPhotoUrls";
import { useLocatieUrl } from "./locatie";
import { formatEuro } from "../../i18n/format";
import { geslachtTeken, leeftijd, type Context } from "../pensionklant/hulp";
import { maakVerzoek, useBerekening, type Keuzes, type WizardGegevens } from "./aanvraag";
import type { Extras } from "./ArrangementenStap";
import { KlantBalk, Terug, Voet, Volgende, langeDatum, useKlantDieren, type GekozenKlant, type Periode, type Vertaal } from "./bouwstenen";
import { EigenElementen, GEEN_ANTWOORDEN, NIETS, TekstBlok, VerplichtMelding, antwoordTekst, useVerplicht } from "./EigenElementen";
import {
  antwoordSleutel,
  blokAan,
  blokElement,
  blokTitel,
  blokUitleg,
  eigenElementen,
  stapKop,
  stapNaam,
  stapVan,
  standaardIndeling,
  type Antwoorden,
  type StapSoort,
  type WizardStap,
} from "./indeling";
import { Samenvatting } from "./Samenvatting";
import type { DierVerblijf } from "./VerblijfStap";

const TEKST_MAX = 500;
const STANDAARD = standaardIndeling();
const STANDAARD_KEUZES = stapVan(STANDAARD, "KEUZES")!;
const STANDAARD_CONTROLE = stapVan(STANDAARD, "CONTROLE")!;

interface Gedeeld {
  klant: GekozenKlant;
  dierIds: number[];
  periode: Periode;
  verblijf: Record<number, DierVerblijf>;
  extras: Extras;
  keuzes: Keuzes;
  ctx: Context;
  onKlant: () => void;
  /** De indeling en de antwoorden; zonder is het de standaardwizard. */
  wizard?: WizardGegevens;
  setAntwoorden?: Dispatch<SetStateAction<Antwoorden>>;
}

function Kaart({ icoon, titel, uitleg, children }: { icoon: ReactNode; titel: string; uitleg: string; children: ReactNode }) {
  return (
    <section className="nr-keuze" aria-label={titel}>
      <span className="nr-rond nr-rond--groot" aria-hidden="true">
        {icoon}
      </span>
      <div className="nr-keuze__tekst">
        <h4>{titel}</h4>
        {uitleg && <p>{uitleg}</p>}
      </div>
      <div className="nr-keuze__veld">{children}</div>
    </section>
  );
}

/**
 * Stap Reserveringskeuzes: de annuleringsverzekering, de wensen van de klant, een interne notitie en de eigen vragen,
 * in de volgorde van de indeling.
 */
export function KeuzesStap({
  klant,
  dierIds,
  periode,
  verblijf,
  extras,
  keuzes,
  setKeuzes,
  ctx,
  onKlant,
  onTerug,
  onVolgende,
  volgendeLabel,
  wizard,
  setAntwoorden = NIETS,
  stap = STANDAARD_KEUZES,
}: Gedeeld & {
  setKeuzes: Dispatch<SetStateAction<Keuzes>>;
  onTerug: () => void;
  onVolgende: () => void;
  volgendeLabel?: string;
  stap?: WizardStap;
}) {
  const t = ctx.t as Vertaal;
  const { locale } = ctx;
  const berekening = useBerekening(maakVerzoek(klant, dierIds, periode, verblijf, extras, keuzes, wizard), periode);
  const b = berekening.data?.data;
  const zet = <K extends keyof Keuzes>(k: K, v: Keuzes[K]) => setKeuzes((x) => ({ ...x, [k]: v }));
  const antwoorden = wizard?.antwoorden ?? GEEN_ANTWOORDEN;
  const { dieren } = useKlantDieren(klant.id);
  const gekozen = dieren.filter((d) => dierIds.includes(d.id));
  const verplicht = useVerplicht(stap, antwoorden, dierIds);
  const kop = stapKop(t, stap, "keuzes");

  return (
    <>
      <div className="nr__inhoud">
        <KlantBalk klant={klant} knop={t("nieuweReservering.periode.klantBekijken")} knopIcoon={<UserRound size={16} aria-hidden="true" />} onKnop={onKlant} />
        <div className="nr-afronden">
          <div className="nr-afronden__links">
            <h3 className="nr-kaart__titel">{kop.titel}</h3>
            <p className="nr-kaart__uitleg">{kop.uitleg}</p>
            {stap.elementen
              .filter((e) => e.actief)
              .map((e) => {
                if (e.soort !== "BLOK") {
                  return (
                    <EigenElementen
                      key={e.id}
                      elementen={[e]}
                      dieren={gekozen}
                      antwoorden={antwoorden}
                      setAntwoorden={setAntwoorden}
                      t={t}
                      geprobeerd={verplicht.geprobeerd}
                    />
                  );
                }
                const titel = blokTitel(t, "KEUZES", e);
                const uitleg = blokUitleg(t, "KEUZES", e);
                switch (e.blok) {
                  case "VERZEKERING": {
                    // De verzekering, en daarnaast elk ander reserveringsproduct dat de vestiging aanbiedt (medifonds,
                    // borg, …), elk als eigen keuze met zijn prijs bij deze reservering.
                    const producten = Array.isArray(b?.producten) ? b.producten : [];
                    const kies = (id: number, aan: boolean) =>
                      setKeuzes((x) => {
                        const nu = (x.producten ?? []).filter((p) => p !== id);
                        return { ...x, producten: aan ? [...nu, id] : nu };
                      });
                    return (
                      <Fragment key={e.id}>
                        {b?.verzekeringAangeboden && (
                          <Kaart icoon={<ShieldCheck size={24} />} titel={titel} uitleg={uitleg}>
                            <div className="nr-radios" role="radiogroup" aria-label={titel}>
                              <label className="nr-radio">
                                <input type="radio" name="verzekering" checked={keuzes.annuleringsverzekering} onChange={() => zet("annuleringsverzekering", true)} />
                                <span>{t("nieuweReservering.keuzes.verzekeringJa")}</span>
                                <strong>{b.verzekeringPrijs != null ? formatEuro(b.verzekeringPrijs, locale) : ""}</strong>
                              </label>
                              <label className="nr-radio">
                                <input type="radio" name="verzekering" checked={!keuzes.annuleringsverzekering} onChange={() => zet("annuleringsverzekering", false)} />
                                <span>{t("nieuweReservering.keuzes.verzekeringNee")}</span>
                              </label>
                            </div>
                          </Kaart>
                        )}
                        {producten.map((p) => {
                          const gekozen = (keuzes.producten ?? []).includes(p.id);
                          return (
                            <Kaart key={p.id} icoon={<ShieldCheck size={24} />} titel={p.naam}
                              uitleg={t("nieuweReservering.keuzes.productUitleg", { args: [p.naam] })}>
                              <div className="nr-radios" role="radiogroup" aria-label={p.naam}>
                                <label className="nr-radio">
                                  <input type="radio" name={`product-${p.id}`} checked={gekozen} onChange={() => kies(p.id, true)} />
                                  <span>{t("nieuweReservering.keuzes.productJa")}</span>
                                  <strong>{formatEuro(p.prijs, locale)}</strong>
                                </label>
                                <label className="nr-radio">
                                  <input type="radio" name={`product-${p.id}`} checked={!gekozen} onChange={() => kies(p.id, false)} />
                                  <span>{t("nieuweReservering.keuzes.productNee")}</span>
                                </label>
                              </div>
                            </Kaart>
                          );
                        })}
                      </Fragment>
                    );
                  }
                  case "WENSEN":
                    return (
                      <Kaart key={e.id} icoon={<MessageSquare size={24} />} titel={titel} uitleg={uitleg}>
                        <textarea
                          className="nr-invoer nr-invoer--hoog"
                          aria-label={titel}
                          maxLength={TEKST_MAX}
                          placeholder={t("nieuweReservering.keuzes.wensenPlaats")}
                          value={keuzes.overigeWensen}
                          onChange={(ev) => zet("overigeWensen", ev.target.value)}
                        />
                        <span className="nr-teller" aria-hidden="true">
                          {keuzes.overigeWensen.length}/{TEKST_MAX}
                        </span>
                      </Kaart>
                    );
                  case "NOTITIE":
                    return (
                      <Kaart key={e.id} icoon={<FileText size={24} />} titel={titel} uitleg={uitleg}>
                        <textarea
                          className="nr-invoer nr-invoer--hoog"
                          aria-label={titel}
                          maxLength={TEKST_MAX}
                          placeholder={t("nieuweReservering.keuzes.notitiePlaats")}
                          value={keuzes.interneNotitie}
                          onChange={(ev) => zet("interneNotitie", ev.target.value)}
                        />
                        <span className="nr-teller" aria-hidden="true">
                          {keuzes.interneNotitie.length}/{TEKST_MAX}
                        </span>
                      </Kaart>
                    );
                  case "INFO":
                    return <TekstBlok key={e.id} titel={e.titel} tekst={uitleg} />;
                  default:
                    return null;
                }
              })}
            <VerplichtMelding open={verplicht.open} geprobeerd={verplicht.geprobeerd} t={t} />
          </div>
          <Samenvatting klant={klant} dierIds={dierIds} periode={periode} berekening={b} laden={berekening.isFetching} ctx={ctx} />
        </div>
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug} label={wizard ? undefined : t("nieuweReservering.keuzes.terug")} />}
        rechts={
          <Volgende t={t} onClick={verplicht.verder(onVolgende)} label={volgendeLabel ?? t("nieuweReservering.keuzes.volgende")} />
        }
      />
    </>
  );
}

interface Opties {
  borstelen: { id: number; naam: string }[];
  wassen: { id: number; naam: string }[];
  voeding: { id: number; naam: string }[];
}

/** De arrangementen en extra's van één dier (of van de hele reservering), met een knop naar die stap. */
function ExtrasVan({
  titel,
  extras,
  onWijzig,
  eur,
  t,
}: {
  titel: string;
  extras: { arrangementId: number; aantal: number; naam: string; bedrag: number }[];
  onWijzig?: () => void;
  eur: (n: number) => string;
  t: Vertaal;
}) {
  return (
    <div className="nr-controle-extras">
      <div className="nr-controle-extras__kop">
        <span>{titel}</span>
        {onWijzig && (
          <button
            type="button"
            className="nr-link"
            onClick={onWijzig}
            aria-label={t("nieuweReservering.controle.wijzigAria", { args: [titel] })}
          >
            <Pencil size={13} aria-hidden="true" />
            {t("nieuweReservering.controle.wijzig")}
          </button>
        )}
      </div>
      {extras.length === 0 ? (
        <span className="nr-klein">{t("nieuweReservering.controle.geenExtras")}</span>
      ) : (
        extras.map((e) => (
          <span key={e.arrangementId} className="nr-som__regel">
            <span>{e.aantal}×</span>
            <span className="nr-som__naam">{e.naam}</span>
            <span>{eur(e.bedrag)}</span>
          </span>
        ))
      )}
    </div>
  );
}

function Blok({ icoon, titel, onWijzig, t, children }: { icoon: ReactNode; titel: string; onWijzig?: () => void; t: Vertaal; children: ReactNode }) {
  return (
    <section className="nr-controleblok" aria-label={titel}>
      <span className="nr-rond nr-rond--groot" aria-hidden="true">
        {icoon}
      </span>
      <div className="nr-controleblok__inhoud">
        <h4>{titel}</h4>
        {children}
      </div>
      {onWijzig && (
        <button type="button" className="nr-knop nr-knop--rand" onClick={onWijzig} aria-label={t("nieuweReservering.controle.wijzigAria", { args: [titel] })}>
          <Pencil size={15} aria-hidden="true" />
          {t("nieuweReservering.controle.wijzig")}
        </button>
      )}
    </section>
  );
}

/**
 * Stap Controle &amp; aanmaken: alles nog één keer op een rij, met per blok een weg terug naar zijn stap, en de
 * reservering aanmaken als aanvraag of bewaren als concept. Wat de indeling uit heeft staan, staat hier ook niet.
 */
export function ControleStap({
  klant,
  dierIds,
  periode,
  verblijf,
  extras,
  keuzes,
  ctx,
  onKlant,
  onStap,
  onTerug,
  onAangemaakt,
  wizard,
  setAntwoorden = NIETS,
  stap = STANDAARD_CONTROLE,
  uit,
}: Gedeeld & {
  /** Naar de stap met deze sleutel. */
  onStap: (stapId: string) => void;
  onTerug: () => void;
  onAangemaakt: (resultaat: { id: number; nummer: number | null; concept: boolean }) => void;
  stap?: WizardStap;
  /** Als voorbeeld: niets aanmaken. */
  uit?: boolean;
}) {
  const t = ctx.t as Vertaal;
  const { locale } = ctx;
  const verzoek = maakVerzoek(klant, dierIds, periode, verblijf, extras, keuzes, wizard);
  const berekening = useBerekening(verzoek, periode);
  const b = berekening.data?.data;
  const { dieren } = useKlantDieren(klant.id);
  const gekozen = dieren.filter((d) => dierIds.includes(d.id));
  const fotos = useAnimalPhotoUrls(gekozen.map((d) => d.id));
  const locatieUrl = useLocatieUrl();
  const opties = useCustom<Opties>({ url: locatieUrl("pension/verzorging/opties"), method: "get" }).data?.data;
  const { mutateAsync, isLoading: bezig } = useCustomMutation<{ id: number; nummer: number | null; concept: boolean }>();
  const [fout, setFout] = useState<string | null>(null);
  const antwoorden = wizard?.antwoorden ?? GEEN_ANTWOORDEN;
  const verplicht = useVerplicht(stap, antwoorden, dierIds);
  const kop = stapKop(t, stap, "controle");
  const indeling = wizard?.indeling ?? STANDAARD;
  const naam = (lijst: { id: number; naam: string }[] | undefined, id: string) =>
    id === "" ? t("nieuweReservering.verblijf.nee") : (lijst?.find((o) => String(o.id) === id)?.naam ?? "—");
  const eur = (n: number) => formatEuro(n, locale);
  const stapAan = (soort: StapSoort) => !!stapVan(indeling, soort)?.actief;
  const naarStap = (soort: StapSoort) => () => onStap(stapVan(indeling, soort)?.id ?? soort.toLowerCase());
  const vb = stapVan(indeling, "VERBLIJF");
  const vbAan = (blok: string) => stapAan("VERBLIJF") && blokAan(vb, blok);
  const ks = stapVan(indeling, "KEUZES");
  const keuzeAan = (blok: string) => stapAan("KEUZES") && blokAan(ks, blok);
  const conceptBlok = blokElement(stap, "CONCEPT");
  const infoBlok = blokElement(stap, "INFO");
  const vragen = indeling.stappen
    .filter((s) => s.actief && s.soort !== "CONTROLE")
    .map((s) => ({ stap: s, vragen: eigenElementen(s).filter((e) => e.soort === "VRAAG") }))
    .filter((x) => x.vragen.length > 0);

  const maak = async (concept: boolean) => {
    setFout(null);
    try {
      const res = await mutateAsync({
        url: locatieUrl("pension/reserveringen/nieuw"),
        method: "post",
        values: { ...verzoek, concept },
        successNotification: false,
        errorNotification: false,
      });
      onAangemaakt(res.data);
    } catch (e) {
      const status = (e as { statusCode?: number } | null)?.statusCode;
      setFout(t(status === 400 ? "nieuweReservering.controle.ongeldig" : "nieuweReservering.controle.fout"));
    }
  };

  const voedingTekst = (v: DierVerblijf) =>
    v.voeding === "STANDAARD"
      ? t("nieuweReservering.verblijf.voedingSTANDAARD")
      : v.voeding === "EIGEN"
        ? t("nieuweReservering.verblijf.voedingEIGEN")
        : (opties?.voeding.find((o) => `categorie:${o.id}` === v.voeding)?.naam ?? "—");

  return (
    <>
      <div className="nr__inhoud">
        <KlantBalk klant={klant} knop={t("nieuweReservering.periode.klantBekijken")} knopIcoon={<UserRound size={16} aria-hidden="true" />} onKnop={onKlant} />
        <div className="nr-afronden">
          <div className="nr-afronden__links">
            <h3 className="nr-kaart__titel">{kop.titel}</h3>
            <p className="nr-kaart__uitleg">{kop.uitleg}</p>

            <Blok icoon={<CalendarDays size={22} />} titel={t("nieuweReservering.som.periode")} onWijzig={naarStap("PERIODE")} t={t}>
              <span className="nr-regel">
                {langeDatum(periode.aankomst, locale)} {periode.aankomstTijd} → {langeDatum(periode.vertrek, locale)} {periode.vertrekTijd}
              </span>
              {b && (
                <span className="nr-klein">
                  {b.nachten === 1 ? t("nieuweReservering.periode.nacht") : t("nieuweReservering.periode.nachten", { args: [b.nachten] })}
                </span>
              )}
            </Blok>

            <Blok
              icoon={<PawPrint size={22} />}
              titel={t(stapAan("VERBLIJF") ? "nieuweReservering.controle.dieren" : "nieuweReservering.som.dieren")}
              onWijzig={naarStap(stapAan("VERBLIJF") ? "VERBLIJF" : "DIEREN")}
              t={t}
            >
              {gekozen.map((d) => {
                const v = verblijf[d.id];
                const prijs = b?.dieren.find((x) => x.dierId === d.id);
                return (
                  <div key={d.id} className="nr-controle-dier">
                    <div className="nr-controle-dier__kop">
                      {fotos[String(d.id)] ? (
                        <img className="nr-mini__foto" src={fotos[String(d.id)]} alt="" />
                      ) : (
                        <span className="nr-mini__foto nr-dier__foto--leeg" aria-hidden="true">
                          <PawPrint size={20} />
                        </span>
                      )}
                      <span className="nr-dier__tekst">
                        <strong>
                          {d.naam} {geslachtTeken(d.geslacht)}
                        </strong>
                        <span className="nr-dier__soort">
                          {[d.diersoort, d.ras, d.geslacht, leeftijd(d.geboortedatum, ctx)].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </div>
                    {v && stapAan("VERBLIJF") && (
                      <dl className="nr-controle-dier__gegevens">
                        {vbAan("TYPE_VERBLIJF") && (
                          <>
                            <dt>{t("nieuweReservering.controle.verblijf")}</dt>
                            <dd>{prijs?.accommodatie ?? "—"}</dd>
                          </>
                        )}
                        {vbAan("VOEDING") && (
                          <>
                            <dt>{t("nieuweReservering.verblijf.voeding")}</dt>
                            <dd>{[voedingTekst(v), v.voedingInstructies.trim()].filter(Boolean).join(", ")}</dd>
                          </>
                        )}
                        {vbAan("MEDICATIE") && (
                          <>
                            <dt>{t("nieuweReservering.verblijf.medicatie")}</dt>
                            <dd>{v.medicatie ? v.medicatieInstructies.trim() || t("nieuweReservering.verblijf.ja") : t("nieuweReservering.controle.geen")}</dd>
                          </>
                        )}
                        {vbAan("VERZORGING") && (
                          <>
                            <dt>{t("nieuweReservering.verblijf.verzorging")}</dt>
                            <dd>
                              {[
                                `${t("nieuweReservering.verblijf.borstelen")}: ${naam(opties?.borstelen, v.borstelen)}`,
                                `${t("nieuweReservering.verblijf.wassen")}: ${naam(opties?.wassen, v.wassen)}`,
                                `${t("nieuweReservering.verblijf.samen")}: ${v.samenMetGezin ? t("nieuweReservering.verblijf.ja") : t("nieuweReservering.verblijf.nee")}`,
                              ].join(" · ")}
                            </dd>
                          </>
                        )}
                        {vbAan("BIJZONDERHEDEN") && (
                          <>
                            <dt>{t("nieuweReservering.verblijf.bijzonderheden")}</dt>
                            <dd>{v.bijzonderheden.trim() || "—"}</dd>
                          </>
                        )}
                      </dl>
                    )}
                    {stapAan("ARRANGEMENTEN") && (
                      <ExtrasVan
                        titel={t("nieuweReservering.arrangementen.titel")}
                        extras={(b?.extras ?? []).filter((e) => e.dierId === d.id)}
                        onWijzig={naarStap("ARRANGEMENTEN")}
                        eur={eur}
                        t={t}
                      />
                    )}
                  </div>
                );
              })}
              {stapAan("ARRANGEMENTEN") && (b?.extras ?? []).some((e) => e.dierId == null) && (
                <div className="nr-controle-dier">
                  <ExtrasVan
                    titel={t("nieuweReservering.arrangementen.reservering")}
                    extras={(b?.extras ?? []).filter((e) => e.dierId == null)}
                    onWijzig={naarStap("ARRANGEMENTEN")}
                    eur={eur}
                    t={t}
                  />
                </div>
              )}
            </Blok>

            <Blok
              icoon={<FileText size={22} />}
              titel={t("nieuweReservering.keuzes.titel")}
              onWijzig={stapAan("KEUZES") ? naarStap("KEUZES") : undefined}
              t={t}
            >
              <dl className="nr-controle-dier__gegevens">
                {keuzeAan("VERZEKERING") && b?.verzekeringAangeboden && (
                  <>
                    <dt>{t("nieuweReservering.keuzes.verzekering")}</dt>
                    <dd>{keuzes.annuleringsverzekering ? t("nieuweReservering.verblijf.ja") : t("nieuweReservering.verblijf.nee")}</dd>
                  </>
                )}
                {keuzeAan("VERZEKERING") && (Array.isArray(b?.producten) ? b.producten : []).map((p) => (
                  <Fragment key={p.id}>
                    <dt>{p.naam}</dt>
                    <dd>{p.gekozen ? t("nieuweReservering.verblijf.ja") : t("nieuweReservering.verblijf.nee")}</dd>
                  </Fragment>
                ))}
                {keuzeAan("WENSEN") && (
                  <>
                    <dt>{t("nieuweReservering.keuzes.wensen")}</dt>
                    <dd>{keuzes.overigeWensen.trim() || "—"}</dd>
                  </>
                )}
                {keuzeAan("NOTITIE") && (
                  <>
                    <dt>{t("nieuweReservering.keuzes.notitie")}</dt>
                    <dd>{keuzes.interneNotitie.trim() || "—"}</dd>
                  </>
                )}
                <dt>{t("nieuweReservering.controle.type")}</dt>
                <dd>{t("nieuweReservering.controle.aanvraag")}</dd>
                <dt>{t("nieuweReservering.controle.status")}</dt>
                <dd>
                  <span className="nr-chip" data-toon="groen">
                    {t("nieuweReservering.controle.ontvangen")}
                  </span>
                </dd>
              </dl>
            </Blok>

            {vragen.map(({ stap: s, vragen: lijst }) => (
              <Blok
                key={s.id}
                icoon={<HelpCircle size={22} />}
                titel={t("nieuweReservering.controle.vragen", { args: [stapNaam(t, s)] })}
                onWijzig={() => onStap(s.id)}
                t={t}
              >
                <dl className="nr-controle-dier__gegevens">
                  {lijst.flatMap((e) =>
                    (e.perDier ? gekozen.map((d) => ({ dier: d.naam ?? String(d.id), sleutel: antwoordSleutel(e.id, d.id) })) : [{ dier: null, sleutel: e.id }]).map(
                      ({ dier, sleutel }) => {
                        const w = antwoorden[sleutel];
                        return (
                          <Fragment key={sleutel}>
                            <dt>{dier ? `${e.titel} – ${dier}` : e.titel}</dt>
                            <dd>{w && w !== "[]" ? antwoordTekst(e.type, w, t, locale) : "—"}</dd>
                          </Fragment>
                        );
                      },
                    ),
                  )}
                </dl>
              </Blok>
            ))}

            <EigenElementen
              elementen={eigenElementen(stap)}
              dieren={gekozen}
              antwoorden={antwoorden}
              setAntwoorden={setAntwoorden}
              t={t}
              geprobeerd={verplicht.geprobeerd}
            />
            {infoBlok?.actief !== false && <TekstBlok titel={infoBlok?.titel} tekst={infoBlok ? blokUitleg(t, "CONTROLE", infoBlok) : t("nieuweReservering.controle.info")} />}
            <VerplichtMelding open={verplicht.open} geprobeerd={verplicht.geprobeerd} t={t} />
            {fout && (
              <p className="nr-melding" role="alert">
                {fout}
              </p>
            )}
          </div>
          <Samenvatting klant={klant} dierIds={dierIds} periode={periode} berekening={b} laden={berekening.isFetching} ctx={ctx} uitgebreid />
        </div>
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug} label={wizard ? undefined : t("nieuweReservering.controle.terug")} />}
        rechts={
          <>
            {(conceptBlok?.actief ?? true) && (
              <button type="button" className="nr-knop nr-knop--rand" disabled={bezig || uit} onClick={() => void maak(true)}>
                <Save size={16} aria-hidden="true" />
                {conceptBlok ? blokTitel(t, "CONTROLE", conceptBlok) : t("nieuweReservering.controle.concept")}
              </button>
            )}
            <button
              type="button"
              className="nr-knop nr-knop--hoofd"
              disabled={bezig || !b || uit}
              onClick={verplicht.verder(() => void maak(false))}
            >
              <Send size={16} aria-hidden="true" />
              {t("nieuweReservering.controle.aanmaken")}
            </button>
          </>
        }
      />
    </>
  );
}

/** Na het aanmaken: wat er gebeurd is, en het venster sluiten. */
export function Klaar({ resultaat, ctx, onSluit }: { resultaat: { id: number; nummer: number | null; concept: boolean }; ctx: Context; onSluit: () => void }) {
  const t = ctx.t as Vertaal;
  return (
    <>
      <div className="nr__inhoud">
        <section className="nr-klaar" role="status">
          <span className="nr-rond nr-rond--groot" aria-hidden="true">
            <Check size={28} />
          </span>
          <h3 className="nr-kaart__titel">
            {resultaat.concept ? t("nieuweReservering.klaar.concept") : t("nieuweReservering.klaar.aanvraag")}
          </h3>
          <p className="nr-kaart__uitleg">
            {t("nieuweReservering.klaar.nummer", { args: [resultaat.nummer ?? resultaat.id] })}
          </p>
          <p className="nr-kaart__uitleg">
            {resultaat.concept ? t("nieuweReservering.klaar.conceptUitleg") : t("nieuweReservering.klaar.aanvraagUitleg")}
          </p>
        </section>
      </div>
      <Voet rechts={<Volgende t={t} onClick={onSluit} label={t("nieuweReservering.klaar.sluiten")} />} />
    </>
  );
}
